
import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ArrowRight, Clock, BookOpen, Sparkles, X } from "lucide-react"

const feedsData = [
  {
    id: 1,
    title: "Beyond Adjudication: The Intelligent Automation Revolution for Payers",
    category: "AI Automation",
    full: `The health insurance landscape is at a breaking point. Escalating costs, sophisticated fraud, and cumbersome manual reviews are squeezing profitability. Traditional claims processing slow, error-prone, and reactive is no longer sustainable. Payers need a strategic advantage to transform this operational burden into a competitive edge.
The challenges are clear: fraud, waste, and abuse drain resources, while complex policy enforcement and rising dispute volumes cripple efficiency. Manual processes create bottlenecks, delay settlements, and damage provider relationships.
This is where PETRA delivers a paradigm shift.
KAYAN's PETRA is an end-to-end automation platform engineered to master the entire claim lifecycle. We empower payers with precision, control, and proactive intelligence.
1.  Unbreachable Compliance: Our Medical & Coding Scrubber automatically validates claims against global standards, slashing errors and denials at the source.
2.  Absolute Policy Control: The Custom Validation Engine lets payer configure and automate your unique TOBs and exclusions, embedding your business logic directly into the workflow.
3.  Proactive Intelligence: The AI-Powered Analysis module detects subtle fraud patterns and predicts trends, transforming raw data into actionable, strategic insights.
The Outcome ! 
Imagine faster processing and a reduction in disputes. Imagine your teams liberated from manual reviews to focus on strategic growth. This is the future PETRA delivers.
Stop managing claims. Start managing outcomes.`,
    image: "/photo-1727434032773-af3cd98375ba.jpg",
    gradient: "from-cyan-500/20 to-blue-600/20",
    accentColor: "cyan",
    readTime: "5 min read",
    date: "Today",
    excerpt: "How AI-driven automation is redefining claims adjudication and payer operations.",
  },
  {
    id: 2,
    title: "Break the Rejection Cycle: Secure Your Revenue with Unshakeable Claim Confidence",
    category: "Revenue Protection",
    full: `For providers, claim rejections are more than an administrative headache they are a direct assault on financial stability and operational morale. The tedious cycle of "submit, reject, correct, resubmit" strangles cash flow and consumes valuable staff time. Navigating complex, ever-changing payer-specific rules is a constant, losing battle.
It's time to break free. It's time for QUDRA.
KAYAN's QUDRA is your strategic shield, empowering you to submit flawless claims with precision and confidence. We ensure your revenue cycle flows smoothly and predictably.
QUDRA delivers a multi-layered defense:
1.  Pre-Emptive Error Elimination: Our Real-Time Claim Review scrubs every claim against coding standards before submission, providing clear alerts for instant correction.
2.  Payer-Specific Automation: The Customizable Rules engine adapts validation to each insurer's policies, ensuring your claims are formatted for first-pass approval.
3.  Deep Performance Insights: AI-Powered Dashboards reveal denial causes and coder patterns, providing the intelligence to optimize your entire billing process.
The Result! 
 Achieve a reduction in rejections and accelerate payments. Reclaim your team's time and secure your financial velocity.
Stop leaving revenue on the table. Start securing it with QUDRA.`,
    image: "/healthcare-automation.png",
    gradient: "from-emerald-500/20 to-teal-600/20",
    accentColor: "emerald",
    readTime: "4 min read",
    date: "Yesterday",
    excerpt: "Strategies to eliminate claim rejections and secure predictable revenue flow.",
  },
 {
    id: 3,
    title: "From Data to Foresight: The Non-Negotiable Power of AI in Healthcare Claims",
    category: "Predictive Intelligence",
    full: `Healthcare generates millions of data points every day, yet many payers still depend on retrospective tools that only reveal what happened after funds have been spent. Fraud, waste, and inefficiency surface too late, and decisions are often based on fragmented reports rather than real intelligence. In today's complex landscape, speed is insufficient—organizations need foresight.

KAYAN transforms raw claims data into intelligent, proactive control through three foundational pillars: Predictive Fraud Detection, Intelligent Trend Analysis, and Actionable Intelligence.

1. Predictive Fraud Detection: From "Pay & Chase" to "Prevent by Design"
Traditional models pay first and investigate later. KAYAN overturns this approach. Its machine learning engines continuously learn what "normal" behavior looks like across services, providers, specialties, and member groups. Each claim is scored in real time, flagging anomalies such as unusual quantities, improper coding, duplicated billing, and sudden cost spikes. High-risk claims are held for review, while low-risk claims flow seamlessly. This targeted focus reduces financial leakage, strengthens oversight, and acts as a clear deterrent against abuse.

2. Intelligent Trend Analysis: Understanding the "Why," Not Just the "What"
Most dashboards show rising costs—few explain their root causes. KAYAN identifies trends across time, regions, specialties, and member segments, revealing seasonal disease patterns, high-cost providers, benefit structures that drive overuse, and emerging risks in therapies. By converting past data into accurate forecasts, KAYAN enables proactive planning: optimizing networks, adjusting contracts, and redesigning benefits before costs escalate.

3. Actionable Intelligence: Insight Designed for Every Role
Data only matters when teams can act on it. KAYAN provides role-specific dashboards, KPIs, risk scores, and prioritized worklists. Executives see high-level indicators and prevented losses; clinicians view treatment patterns; operations teams receive clear task priorities. Visual cues remove guesswork, accelerating decision-making and reducing investigation time.

The Bottom Line
The complexity and speed of modern healthcare make manual, rule-only approaches obsolete. With KAYAN, claims are no longer a back-office burden, they become a strategic asset that protects finances, strengthens provider networks, and supports better care.

You already have the data. AI is what turns it into foresight. With KAYAN, your claims data becomes your greatest strategic asset—not your biggest headache.`,
    image: "/premium_photo-1669842504837-ac6c1bad2bcf.jpg",
    gradient: "from-purple-500/20 to-pink-600/20",
    accentColor: "purple",
    readTime: "6 min read",
    date: "2 days ago",
    excerpt: "Turning healthcare data into predictive intelligence for better decisions.",
  },
  {
    id: 4,
    title: "The Next Frontier in Digital Health: Intelligent E-Prescription",
    category: "Digital Healthcare",
   full: `Smarter PBM, Safer Prescriptions, Stronger Claims.

Prescription errors are more than clinical mistakes — they are a direct threat to patient safety, provider efficiency, and payer stability.

The constant cycle of “prescribe, reject, correct, resubmit” slows treatment, frustrates teams, and increases risk across the entire healthcare chain.

Managing clinical rules, drug interactions, and payer requirements manually is a losing battle.

It’s time to break the cycle.
It’s time for Intelligent PBM.

KAYAN’s PBM Drug-Check Engine is your clinical and operational shield — ensuring every prescription is accurate, safe, and instantly payable. We make prescribing faster, safer, and drastically more reliable.

Our engine delivers a multi-layered defense:

1. Diagnostic Precision:
Validates every medication against approved indications, flags missing or incorrect diagnoses, and prevents contraindicated therapies before they reach the pharmacy.

2. Interaction & Safety Intelligence:
Screens all active medications in real time, detects harmful drug–drug interactions, and recommends safer alternatives — stopping dangerous combinations before they occur.

3. Dose Accuracy & Compliance:
Applies age-specific, gender-specific, and guideline-based dosing rules to prevent overdosing, under-dosing, or off-protocol use.

4. Benefit Checks:
Beyond clinical safety, KAYAN’s PBM engine embeds benefit validation that protects payers, ensures policy compliance, and eliminates unnecessary costs before a claim is submitted. This includes:
• Drug Exclusions: Detects medications not covered under the patient’s plan
• Days’ Supply Limit: Controls maximum allowable days dispensed
• Refill Limitations: Ensures refills follow policy rules and timing
• Age & Gender Restrictions: Applies non-clinical benefit rules based on policy
• Generic Substitutes Check: Enforces use of generics when available
• Drugs Requiring Prior Authorization: Flags medications needing manual review
• Lab Results Flag: Highlights drugs requiring specific lab tests
• Prescriber Restrictions: Confirms only allowed prescriber classes can issue certain medications
• Step Therapy: Enforces required first-line treatments before approving alternatives

What this achieves:
• Fewer denials and resubmissions
• Full network, policy, and formulary compliance
• Significant cost control and fraud reduction

The Result
Safer prescribing. Fewer denials. Stronger control. Faster access to the right medications.

With KAYAN’s Intelligent PBM Drug-Check Engine, healthcare stakeholders eliminate clinical errors, protect financial outcomes, reduce unnecessary operational load, and deliver safer, faster, more reliable patient care.

Stop risking safety and revenue with manual checks.
Start securing both with Kayan’s Intelligent PBM Drug-Check Engine.`,

    image: "/premium_photo-1698421947098-d68176a8f5b2.jpg",
    gradient: "from-blue-500/20 to-indigo-600/20",
    accentColor: "blue",
    readTime: "3 min read",
    date: "Last Week",
    excerpt: "How intelligent e-prescription is shaping the future of pharma automation.",
  },
]

export default function FeedsSection() {
 const [modalFeed, setModalFeed] = useState(null)
const [hoveredCard, setHoveredCard] = useState(null)

  const accentColors = {
    cyan: {
      border: "border-cyan-400/50",
      button: "bg-cyan-500 hover:bg-cyan-600",
    },
    emerald: {
      border: "border-emerald-400/50",
      button: "bg-emerald-500 hover:bg-emerald-600",
    },
    purple: {
      border: "border-purple-400/50",
      button: "bg-purple-500 hover:bg-purple-600",
    },
    blue: {
      border: "border-blue-400/50",
      button: "bg-blue-500 hover:bg-blue-600",
    },
  }

  //bahaa
  return (
    <section className="relative min-h-screen overflow-hidden py-16 sm:py-20 md:py-24 px-4 sm:px-6">

      <div className="absolute inset-0 mesh-gradient opacity-70" />

<div className="max-w-full mx-auto w-full relative z-10">

        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >

          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full glass-light mb-3 border border-cyan-500/30">
            <Sparkles className="w-4 h-5 text-cyan-400" />
            <span className="text-sm font-medium text-gray-200 uppercase tracking-wider">Latest Insights</span>
          </div>

          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6">
            Our Feeds
          </h2>

          <p className="text-gray-400 text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed px-4 sm:px-0">
            Discover the latest innovations, insights, and trends shaping the future of healthcare technology
          </p>

        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5 max-w-7xl mx-auto px-2 sm:px-0">

          {feedsData.map((feed, index) => {
const colors = accentColors[feed.accentColor]

            return (
              <motion.div
                key={feed.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: index * 0.15 }}
                viewport={{ once: true }}
                onHoverStart={() => setHoveredCard(feed.id)}
                onHoverEnd={() => setHoveredCard(null)}
                onClick={() => setModalFeed(feed)}
                className="relative rounded-xl overflow-hidden group cursor-pointer transition-all duration-500 h-[280px] sm:h-[320px] md:h-[360px] lg:h-[380px]"
              >

                <motion.div
                  className="absolute inset-0"
                  animate={{ scale: hoveredCard === feed.id ? 1.05 : 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <img src={feed.image} alt={feed.title} className="w-full h-full object-cover" />
                </motion.div>

                <div className={`absolute inset-0 bg-gradient-to-t ${feed.gradient} via-black/60 to-black/80`} />

                <div className={`absolute inset-0 border-2 ${colors.border} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl`} />

                <div className="absolute inset-0 p-3 sm:p-4 flex flex-col justify-between">

                  <div>
                    <h3 className="text-sm sm:text-base md:text-lg font-bold text-white mb-2 leading-tight">
                      {feed.title}
                    </h3>
                    <p className="text-gray-300 text-xs leading-relaxed line-clamp-2">
                      {feed.excerpt}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-gray-400 text-xs">
                      <Clock className="w-3 h-3" />
                      <span>{feed.readTime}</span>
                      <span>•</span>
                      <span>{feed.date}</span>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02, x: 2 }}
                      whileTap={{ scale: 0.97 }}
                      className={`flex items-center gap-1.5 ${colors.button} px-3 py-1.5 sm:px-4 sm:py-2 rounded-md text-white font-semibold text-xs shadow-lg transition-all`}
                    >
                      <BookOpen className="w-3.5 h-3.5" />
                      <span>Read Article</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </motion.button>
                  </div>

                </div>

              </motion.div>
            )
          })}

        </div>
      </div>

      <AnimatePresence>
        {modalFeed && (
          <motion.div
            className="fixed inset-0 bg-black/90 backdrop-blur-xl z-[9999] flex items-center justify-center p-4"
            onClick={() => setModalFeed(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >

      <motion.div
  className="relative w-full max-w-4xl bg-gray-900 rounded-2xl shadow-2xl p-6 sm:p-10 max-h-[85vh] overflow-y-auto"
  onClick={(e) => e.stopPropagation()}
  initial={{ scale: 0.9, y: 20, opacity: 0 }}
  animate={{ scale: 1, y: 0, opacity: 1 }}
  exit={{ scale: 0.9, y: 20, opacity: 0 }}
  style={{
    scrollbarWidth: 'thin',
    scrollbarColor: '#06b6d4 #1f2937'
  }}
>

  <button
    onClick={() => setModalFeed(null)}
    className="absolute top-4 right-4 text-white hover:text-cyan-400 z-10"
  >
    <X />
  </button>

  <span className="inline-block mb-4 text-cyan-400 text-sm">{modalFeed.category}</span>

  <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">{modalFeed.title}</h2>

  <p className="text-gray-300 whitespace-pre-line text-sm sm:text-base leading-relaxed">
    {modalFeed.full}
  </p>

</motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </section>
  )
}
